import { Button, Typography } from "@mui/material";
import { Box, Stack } from "@mui/system";
import { Workstation24Px, AlertBanner } from "@qubit/autoparts";

import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";
import { ASDSThemeProvider } from "~/components/designSystem/ASDSThemeProvider";
import { useFcNavigate } from "~/hooks/useFcNavigate";

import { selectThisWorkstation } from "~/redux/selectors/workstationsSelectors";

import EventFeed from "./EventFeed";

type TestingToolLayoutProps = {
  children: React.ReactNode;
};

const TestingToolLayout = (props: TestingToolLayoutProps) => {
  const { children } = props;
  const navigate = useFcNavigate();
  const { t } = useTranslation();
  const workstation = useAppSelector(selectThisWorkstation);
  const shouldRenderNoWsMessage = !workstation;

  return (
    <Box
      sx={[
        {
          // Fill remaining viewport below a fixed 150px header
          height: "calc(100vh - 150px)",
          // Two-row & two-column grid
          // Test content on left, event feed on bottom-right
          display: "grid",
          justifyItems: "center",
          alignItems: "center",
          gridAutoFlow: "column",
          gridTemplateRows: "auto 1fr",
          gridTemplateColumns: "1fr minmax(350px, 0.3fr)", // main area + event feed sidebar
          gap: 2,
          overflow: "hidden",
          "@media (max-width: 1200px)": {
            // Drop fixed height, stack into one column
            height: "auto",
            minHeight: "calc(100vh - 150px)",
            gridTemplateColumns: "1fr",
            gap: 1
          }
        }
      ]}
    >
      {/* Main Testing Tool content */}
      <Box
        sx={{
          height: "100%",
          width: "100%",
          gridColumn: "1/2",
          gridRow: "1/3",
          boxSizing: "border-box",
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            display: "none"
          },
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "@media (max-width: 1200px)": {
            // this becomes the first full-width row
            gridColumn: "1/-1",
            gridRow: "1/2"
          },
          ...(shouldRenderNoWsMessage && {
            display: "flex",
            justifyContent: "center"
          })
        }}
      >
        {shouldRenderNoWsMessage ? (
          <Stack
            sx={{
              flexDirection: "column",
              gap: 1,
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              "@media (max-width: 1200px)": {
                my: 3,
                gap: 0
              }
            }}
            role="region"
            aria-label="No workstation selected message"
          >
            <ASDSThemeProvider>
              <AlertBanner>{`${t("no workstation selected")}`}</AlertBanner>
            </ASDSThemeProvider>
            <Button
              color="info"
              sx={{
                minWidth: 250,
                minHeight: 70,
                fontSize: "1.5rem",
                mt: 3
              }}
              onClick={() =>
                navigate("/settings?settings-tab=workstation-config")
              }
              startIcon={<Workstation24Px fill="white" />}
            >
              {t("assign workstation")}
            </Button>
            <Typography
              variant="subtitle1"
              fontStyle="italic"
              fontWeight={400}
              textAlign="center"
            >
              {t("workstation warning info")}
            </Typography>
          </Stack>
        ) : (
          children
        )}
      </Box>
      {/*Event feed sidebar */}
      <EventFeed />
    </Box>
  );
};

export default TestingToolLayout;
